import { resetRun, type GameSim } from "./sim";
import { TRACK_LENGTH } from "./constants";
import { audioContextState, setAmbienceLevel } from "./audio";
import { musicDebugState } from "./music";
import { getObject } from "./objects";
import { setFullResolution } from "./gl/quality";
import {
  clearInjectedKeys,
  readMoveX,
  readTuck,
  setInjectedKeys,
  setSteerOverride,
  setTuckOverride,
} from "./input";

declare global {
  interface Window {
    __controlsTest?: {
      getYaw: () => number;
      getSpeed: () => number;
      getX?: () => number;
      getY?: () => number;
      getPhase?: () => string;
      getMoveX?: () => number;
      getTuck?: () => boolean;
      getStun?: () => number;
      getHotDogs?: () => number;
      getBurgers?: () => number;
      getRamps?: () => number;
      getCrashes?: () => number;
      getGrazes?: () => number;
      getAudioState?: () => string;
      getMusic?: () => { theme: string; mode: string; on: boolean };
      nextPickup?: () => { x: number; y: number } | null;
      setSteer?: (v: number) => void;
      setTuck?: (v: boolean | null) => void;
      setKeys?: (codes: string[]) => void;
      finishNow?: () => void;
      getSeed?: () => number;
      getTime?: () => number;
      getHeight?: () => number;
      reseed?: (seed: number) => void;
      setFullResolution?: (on: boolean) => void;
      setAmbience?: (level: number) => void;
    };
  }
}

export function wireControlsTest(sim: GameSim): void {
  const enable =
    import.meta.env.DEV ||
    (typeof window !== "undefined" && new URLSearchParams(window.location.search).has("qa"));
  if (!enable || typeof window === "undefined") return;
  window.__controlsTest = {
    getYaw: () => -sim.player.x * 0.01,
    getSpeed: () => sim.player.speedY,
    getX: () => sim.player.x,
    getY: () => sim.player.y,
    getPhase: () => sim.phase,
    getMoveX: () => readMoveX(),
    getTuck: () => readTuck(),
    getStun: () => sim.player.stun,
    getHotDogs: () => sim.hotDogs,
    getBurgers: () => sim.burgers,
    getRamps: () => sim.ramps,
    getCrashes: () => sim.crashes,
    getGrazes: () => sim.grazes,
    getAudioState: () => audioContextState(),
    getMusic: () => musicDebugState(),
    // The nearest uncollected pickup ahead of the rider. finishNow() teleports
    // straight past every pickup on the hill, so a test that wants to prove
    // collection works has to steer onto one, and it needs to know where.
    nextPickup: () => {
      let best: { x: number; y: number } | null = null;
      for (const e of sim.entities) {
        if (e.consumed || getObject(e.defId).collision !== "pickup") continue;
        if (e.y <= sim.player.y) continue;
        if (!best || e.y < best.y) best = { x: e.x, y: e.y };
      }
      return best;
    },
    setSteer: (v: number) => setSteerOverride(v),
    setTuck: (v: boolean | null) => setTuckOverride(v),
    setKeys: (codes: string[]) => {
      setSteerOverride(null);
      if (codes.length === 0) {
        clearInjectedKeys();
      } else {
        setInjectedKeys(codes);
      }
    },
    finishNow: () => {
      sim.hitstop = 0;
      sim.player.y = TRACK_LENGTH + 4;
    },
    getSeed: () => sim.seed,
    getTime: () => sim.time,
    getHeight: () => sim.player.height,
    reseed: (seed: number) => resetRun(sim, seed),
    setFullResolution: (on: boolean) => setFullResolution(on),
    setAmbience: (level: number) => setAmbienceLevel(level),
  };
}
